import type { MouseEvent } from "react";
import { Link } from "~/components/ui/link";
import { Trans, useTranslation } from "react-i18next";
import { openCookiePreferences } from "~/lib/analytics/osano";
import {
  RiFacebookFill,
  RiInstagramLine,
  RiLinkedinBoxFill,
  RiYoutubeFill,
} from "@remixicon/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Badge } from "~/components/ui/badge";
import ExternalLinkIcon from "~/assets/icons/ExternalLinkIcon";
import { cn } from "~/lib/utils";
import { useOpenObdCompatibilityChecker } from "~/components/products/pdp/ObdCompatibilityCheckerContext";
import wordmark from "~/assets/logos/bouncie-wordmark-blue.svg";
import wordmarkWhite from "~/assets/logos/bouncie-wordmark-white.svg";
import appleBadgeBackground from "~/assets/footer/apple-badge-background.svg";
import appleBadgeLogo from "~/assets/footer/apple-badge-logo.svg";
import appleBadgeLine1 from "~/assets/footer/apple-badge-line1.svg";
import appleBadgeLine2 from "~/assets/footer/apple-badge-line2.svg";
import googlePlayBadge from "~/assets/footer/google-play-badge.svg";
import { APP_STORE_URL, DEVELOPER_DOCS_URL, GOOGLE_PLAY_URL, HELP_CENTER_URL, STATUS_URL, WEB_APP_SUBSCRIPTIONS_URL, WEB_APP_URL } from "~/lib/external-links";

const socialLinks = [
  {
    icon: RiFacebookFill,
    href: "https://www.facebook.com/BouncieApp",
    label: "Facebook",
  },
  {
    icon: RiInstagramLine,
    href: "https://www.instagram.com/Bouncie.App",
    label: "Instagram",
  },
  {
    icon: RiLinkedinBoxFill,
    href: "https://www.linkedin.com/company/bouncie",
    label: "LinkedIn",
  },
  {
    icon: RiYoutubeFill,
    href: "https://www.youtube.com/bouncie",
    label: "YouTube",
  },
] as const;

interface FooterLink {
  titleKey: string;
  href: string;
  comingSoon?: boolean;
  // Off-site links open in a new tab.
  external?: boolean;
  // Solutions nests segment links under their Consumer/Fleet parent with an 8px indent.
  indent?: boolean;
  // Opens the site-wide OBD Compatibility Checker drawer instead of following href.
  opensObdChecker?: boolean;
}

interface FooterGroup {
  key: string;
  headingKey: string;
  links: FooterLink[];
}

const PRODUCTS: FooterGroup = {
  key: "products",
  headingKey: "footer.products.heading",
  links: [
    { titleKey: "footer.products.drive", href: "/products/drive" },
    {
      titleKey: "footer.products.view",
      href: "/products/view",
      comingSoon: true,
    },
    { titleKey: "footer.products.go", href: "/products/go" },
    { titleKey: "footer.products.shop", href: "/products" },
  ],
};

const SOLUTIONS: FooterGroup = {
  key: "solutions",
  headingKey: "footer.solutions.heading",
  links: [
    { titleKey: "nav.personal_use", href: "/personal-use" },
    {
      titleKey: "footer.solutions.households",
      href: "/personal-use/families",
      indent: true,
    },
    {
      titleKey: "subnav.personal_use.teen_drivers.name",
      href: "/personal-use/teen-drivers",
      indent: true,
    },
    {
      titleKey: "footer.solutions.senior_drivers",
      href: "/personal-use/senior-drivers",
      indent: true,
    },
    {
      titleKey: "subnav.personal_use.tech_enthusiasts.name",
      href: "/personal-use/tech-enthusiasts",
      indent: true,
    },
    { titleKey: "nav.fleet", href: "/fleet" },
    {
      titleKey: "footer.solutions.service_business",
      href: "/fleet",
      indent: true,
    },
    {
      titleKey: "subnav.fleet.industry_list.delivery_logistics",
      href: "/fleet",
      indent: true,
    },
    {
      titleKey: "footer.solutions.rental_operations",
      href: "/fleet/rental",
      indent: true,
    },
    {
      titleKey: "subnav.fleet.industry_list.turo_hosts",
      href: "/fleet/turo",
      indent: true,
    },
    {
      titleKey: "subnav.fleet.industry_list.charitable",
      href: "/fleet/non-profit",
      indent: true,
    },
    {
      titleKey: "footer.solutions.public_sector",
      href: "/fleet/municipal",
      indent: true,
    },
  ],
};

const SUPPORT: FooterGroup = {
  key: "support",
  headingKey: "subnav.resources.col_support",
  links: [
    { titleKey: "footer.support.find_obd_port", href: "#", opensObdChecker: true },
    {
      titleKey: "subnav.resources.help_center",
      href: HELP_CENTER_URL,
      external: true,
    },
    {
      titleKey: "subnav.resources.system_status",
      href: STATUS_URL,
      external: true,
    },
  ],
};

const ACCOUNT: FooterGroup = {
  key: "account",
  headingKey: "footer.account.heading",
  links: [
    {
      titleKey: "footer.account.manage_account",
      href: WEB_APP_URL,
      external: true,
    },
    {
      titleKey: "footer.account.manage_subscription",
      href: WEB_APP_SUBSCRIPTIONS_URL,
      external: true,
    },
  ],
};

const COMPANY: FooterGroup = {
  key: "company",
  headingKey: "footer.company.heading",
  links: [
    {
      titleKey: "subnav.resources.blog",
      href: "https://www.bouncie.com/blog",
      external: true,
    },
    { titleKey: "subnav.resources.careers", href: "/careers" },
    { titleKey: "footer.company.collaborate", href: "/contact" },
  ],
};

const DEVELOPERS: FooterGroup = {
  key: "developers",
  headingKey: "footer.developers.heading",
  links: [
    {
      titleKey: "footer.developers.docs",
      href: DEVELOPER_DOCS_URL,
      external: true,
    },
    {
      titleKey: "footer.developers.api",
      href: DEVELOPER_DOCS_URL,
      external: true,
    },
  ],
};

const ALL_GROUPS = [PRODUCTS, SOLUTIONS, SUPPORT, ACCOUNT, COMPANY, DEVELOPERS];

// Column widths match Figma's fixed column frames so the centered desktop nav lands where the
// design puts it.
const DESKTOP_COLUMNS: {
  key: string;
  className?: string;
  groups: FooterGroup[];
}[] = [
  { key: "products", groups: [PRODUCTS] },
  { key: "solutions", groups: [SOLUTIONS] },
  { key: "support", className: "min-w-[180px]", groups: [SUPPORT, ACCOUNT] },
  { key: "company", className: "min-w-[157px]", groups: [COMPANY, DEVELOPERS] },
];

// Tablet stacks the same groups into two columns; the second column is Figma's 180px frame.
const TABLET_COLUMNS: { key: string; groups: FooterGroup[] }[] = [
  { key: "primary", groups: [PRODUCTS, SOLUTIONS] },
  { key: "secondary", groups: [SUPPORT, ACCOUNT, COMPANY, DEVELOPERS] },
];

// Links flagged opensObdChecker open the drawer (mounted in root.tsx) in place of navigating.
function useLinkClick(link: FooterLink) {
  const openObdChecker = useOpenObdCompatibilityChecker();
  if (!link.opensObdChecker || !openObdChecker) return undefined;
  return (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openObdChecker();
  };
}

function FooterLinkRow({
  link,
  t,
}: {
  link: FooterLink;
  t: (key: string) => string;
}) {
  const onClick = useLinkClick(link);
  return (
    <Link
      data-sid={`footer-link:${link.href}:${link.titleKey}`}
      to={link.href}
      onClick={onClick}
      {...(link.external ? { target: "_blank", rel: "noreferrer" } : {})}
      className={cn(
        "text-text-body-alt hover:text-text-heading flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm transition-colors",
        link.indent && "pl-2",
      )}
    >
      {/* Long labels (e.g. in French) wrap instead of pushing the footer past the page edge,
          and a badge drops below its label before the label itself breaks. */}
      <span className="min-w-0">{t(link.titleKey)}</span>
      {link.external && <ExternalLinkIcon className="shrink-0" />}
      {link.comingSoon && (
        <Badge
          variant="inverse"
          // Borderless for Figma's 26px height; the negative margin keeps it out of the 20px row height.
          className="rounded-figma-radius-radius-2xl my-[-3px] border-0 whitespace-nowrap"
        >
          {t("footer.products.coming_soon")}
        </Badge>
      )}
    </Link>
  );
}

function ColumnGroup({
  group,
  t,
}: {
  group: FooterGroup;
  t: (key: string) => string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-text-heading text-base font-bold">
        {t(group.headingKey)}
      </h3>
      <ul className="flex flex-col gap-3">
        {group.links.map((link) => (
          <li key={link.titleKey}>
            <FooterLinkRow link={link} t={t} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function MobileLinkRow({
  link,
  t,
}: {
  link: FooterLink;
  t: (key: string) => string;
}) {
  const onClick = useLinkClick(link);
  return (
    <Link
      data-sid={`footer-mobile-link:${link.href}:${link.titleKey}`}
      to={link.href}
      onClick={onClick}
      {...(link.external ? { target: "_blank", rel: "noreferrer" } : {})}
      className={cn(
        "hover:bg-subnav-item-hover text-text-body-alt flex h-12 flex-wrap items-center gap-2 rounded-2xl px-4 text-base font-medium transition-colors",
        link.indent && "pl-6",
      )}
    >
      <span className="min-w-0">{t(link.titleKey)}</span>
      {link.external && <ExternalLinkIcon className="shrink-0" />}
      {link.comingSoon && (
        <Badge
          variant="inverse"
          className="rounded-figma-radius-radius-2xl border-0 whitespace-nowrap"
        >
          {t("footer.products.coming_soon")}
        </Badge>
      )}
    </Link>
  );
}

export type FooterMode = "default" | "fleet";

// "fleet" is Figma's Fleet (Dark Mode) variant: the same footer under the .fleet token scope,
// with the white wordmark.
export default function Footer({ mode = "default" }: { mode?: FooterMode }) {
  const { t } = useTranslation("common");

  return (
    <footer
      className={cn(
        "bg-bg-alt border-border fleet:border-figma-blue-midnight border-t",
        mode === "fleet" && "fleet",
      )}
    >
      <div className="wrap flex flex-col gap-10 py-6 md:py-15 lg:gap-15">
        {/* Tablet: Figma's Solutions frame is fixed at 444px, leaving 32px below its last link
            on top of the 40px section gap — mb-8 restores that 72px before the app badges. */}
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between md:max-lg:mb-8 lg:justify-center lg:gap-8">
          <div className="flex flex-col items-start gap-[22px]">
            <Link to="/" aria-label="Bouncie">
              <img
                src={mode === "fleet" ? wordmarkWhite : wordmark}
                alt="Bouncie"
                className="h-7 w-auto"
              />
            </Link>
            {/* 25px icons size the glyphs to Figma's ~21px; the negative margin cancels the
                Facebook glyph's built-in inset so it lines up with the wordmark. */}
            <div className="ml-[-7px] flex items-center gap-[17.5px]">
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <Link
                  key={label}
                  to={href}
                  aria-label={label}
                  className="text-icon-secondary fleet:text-icon-subtle hover:text-text-link transition-colors"
                >
                  <Icon className="size-[25px]" />
                </Link>
              ))}
            </div>
          </div>

          {/* Figma's 48px column gap only fits from xl; the 976px lg container needs 32px or
              the Products column shrinks and pushes the Coming Soon badge onto its own line. */}
          <nav
            aria-label={t("nav.footer_navigation")}
            className="hidden lg:flex lg:flex-1 lg:justify-center lg:gap-8 xl:gap-12"
          >
            {DESKTOP_COLUMNS.map((column) => (
              <div
                key={column.key}
                className={cn("flex flex-col gap-10", column.className)}
              >
                {column.groups.map((group) => (
                  <ColumnGroup key={group.key} group={group} t={t} />
                ))}
              </div>
            ))}
          </nav>

          <nav
            aria-label={t("nav.footer_navigation")}
            className="hidden md:grid md:w-[481px] md:grid-cols-[1fr_180px] lg:hidden"
          >
            {TABLET_COLUMNS.map((column) => (
              <div key={column.key} className="flex flex-col gap-10">
                {column.groups.map((group) => (
                  <ColumnGroup key={group.key} group={group} t={t} />
                ))}
              </div>
            ))}
          </nav>

          <nav
            aria-label={t("nav.footer_navigation")}
            className="w-full md:hidden"
          >
            <Accordion type="single" collapsible>
              {ALL_GROUPS.map((group) => (
                <AccordionItem
                  key={group.key}
                  value={group.key}
                  className="border-border bg-transparent"
                >
                  <AccordionTrigger data-sid={`footer-section-toggle:${group.headingKey}`}>{t(group.headingKey)}</AccordionTrigger>
                  <AccordionContent className="px-0 pb-3">
                    <ul className="flex flex-col gap-1">
                      {group.links.map((link) => (
                        <li key={link.titleKey}>
                          <MobileLinkRow link={link} t={t} />
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </nav>
        </div>

        <div className="flex items-center justify-center gap-2.5">
          <Link
            to={APP_STORE_URL}
            aria-label={t("footer.app_store")}
          >
            <span className="relative block h-10 w-[135px]">
              <img
                src={appleBadgeBackground}
                alt=""
                className="absolute inset-0 size-full"
              />
              <img
                src={appleBadgeLogo}
                alt=""
                className="absolute top-[9px] left-[10px] h-[22px] w-[18px]"
              />
              <img
                src={appleBadgeLine1}
                alt=""
                className="absolute top-[8px] left-[44px] h-[6px] w-[69px]"
              />
              <img
                src={appleBadgeLine2}
                alt=""
                className="absolute top-[18px] left-[42px] h-[16px] w-[75px]"
              />
            </span>
          </Link>
          <Link
            to={GOOGLE_PLAY_URL}
            aria-label={t("footer.google_play")}
          >
            <img src={googlePlayBadge} alt="" className="h-10 w-[135px]" />
          </Link>
        </div>

        <div className="border-border flex flex-col gap-8 border-t pt-6">
          <div className="text-text-body-secondary flex flex-col gap-5 text-sm leading-5">
            <p>{t("footer.legal.secret_use")}</p>
            <p>{t("footer.legal.impact_detection")}</p>
            <p>
              <Trans
                t={t}
                i18nKey="footer.legal.review"
                components={{
                  privacy: <Link to="/privacy-policy" className="underline" />,
                  terms: <Link to="/terms-of-service" className="underline" />,
                }}
              />
            </p>
          </div>

          <div className="text-text-body-secondary flex flex-col items-start gap-4 text-sm leading-5 md:flex-row md:items-center md:justify-between md:gap-2">
            <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-6">
              <Link to="/terms-of-use" className="underline">
                {t("footer.terms_of_use_privacy")}
              </Link>
              <button
                type="button"
                className="cursor-pointer underline"
                onClick={openCookiePreferences}
              >
                {t("footer.cookie_preferences")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
